<template>
    <el-dialog
        :title="(isForm ? (formId ? '编辑' : '添加') : '查看') + dialogTitle"
        width="640px"
        :model-value="modelValue"
        @close="closed"
        :close-on-click-modal="false"
    >
        <el-form ref="formRef" :model="formData" :rules="isForm ? formRules : []" label-width="120px" style="max-width: 540px">
            <el-row>
                <el-col :span="24">
                    <el-form-item label="套件名称" prop="name">
                        <el-input v-if="isForm" v-model="formData.name" placeholder="请输入套件名称"></el-input>
                        <span v-else>{{ formData.name }}</span>
                    </el-form-item>
                </el-col>
                <el-form-item label="所属分组" prop="group">
                    <el-select v-if="isForm" v-model="formData.group" clearable placeholder="所属分组">
                        <el-option v-for="item in groupList" :key="item.value" :label="item.label" :value="item.value" />
                    </el-select>
                    <span v-else>{{ formData.group_name }}</span>
                </el-form-item>
                <el-col :span="24">
                    <el-form-item label="图标" prop="icon">
                        <div v-if="isForm" class="avatar-upload-box">
                            <el-upload
                                ref="avatarUploader"
                                class="uploader"
                                action="#"
                                list-type="picture-card"
                                :limit="1"
                                :auto-upload="false"
                                :on-exceed="handleAvatarExceed"
                                :on-change="handleAvatarChange"
                                :on-remove="handleAvatarRemove"
                                :file-list="avatarEditLists"
                            >
                                <el-icon class="avatar-uploader-icon">
                                    <plus />
                                </el-icon>
                                <template #tip>
                                    <div class="el-upload__tip">请上传 jpg/png 类型的图片且文件大小不能超过 2Mb.</div>
                                </template>
                            </el-upload>
                        </div>
                        <div v-else>
                            <img :src="formData.icon_url" class="avatar" shape="square" fit="fill" :size="50" alt="" />
                        </div>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="套件类型" prop="kit_type">
                        <el-radio-group v-if="isForm" v-model="formData.kit_type">
                            <el-radio :value="1">本地</el-radio>
                            <el-radio :value="2">外链</el-radio>
                        </el-radio-group>
                        <div v-else>{{ formData.kit_type ? '本地' : '外链' }}</div>
                    </el-form-item>
                </el-col>
                <el-col :span="12" v-if="formData.kit_type === 1" @click="handleSelectModule">
                    <el-form-item label="菜单" prop="auth_name">
                        <template v-if="isForm">
                            <el-input v-model="formData.auth_name" placeholder="请选择菜单"></el-input>
                        </template>
                        <a v-else class="name" @click="urlPush">{{ formData.auth_name }}</a>
                    </el-form-item>
                </el-col>
                <el-col :span="12" v-if="formData.kit_type === 2">
                    <el-form-item label="URL" prop="url">
                        <template #label>
                            <el-tooltip
                                v-if="isForm"
                                class="box-item"
                                effect="dark"
                                content="选择是外链则路由地址需要以`http(s)://`开头"
                                placement="top"
                            >
                                <el-icon>
                                    <QuestionFilled />
                                </el-icon>
                            </el-tooltip>
                            <span>URL</span>
                        </template>
                        <el-input v-if="isForm" v-model="formData.url" placeholder="请输入URL"></el-input>
                        <a v-else class="name" :href="formData.url" target="_blank" style="word-break: break-all">
                            {{ formData.url }}
                        </a>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="排序值" prop="list_order">
                        <el-input-number
                            v-if="isForm"
                            v-model="formData.list_order"
                            class="mx-4"
                            :min="0"
                            :max="999999"
                            controls-position="right"
                        />
                        <span v-else>{{ formData.list_order }}</span>
                    </el-form-item>
                </el-col>

                <el-col :span="12">
                    <el-form-item label="状态">
                        <el-radio-group v-if="isForm" v-model="formData.status">
                            <el-radio :value="1">正常</el-radio>
                            <el-radio :value="0">停用</el-radio>
                        </el-radio-group>
                        <div v-else>{{ formData.status ? '正常' : '停用' }}</div>
                    </el-form-item>
                </el-col>
                <el-col :span="24">
                    <el-form-item label="备注">
                        <el-input v-if="isForm" v-model="formData.remark" type="textarea"></el-input>
                        <div v-else>{{ formData.remark }}</div>
                    </el-form-item>
                </el-col>
            </el-row>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="closed">取消</el-button>
                <el-button v-if="isForm" type="primary" :loading="submitLoading" @click="onConfirm">确定</el-button>
            </span>
        </template>
    </el-dialog>
    <module-dialog
        v-model="moduleDialogVisible"
        :isForm="isForm"
        @updateForm="getAuthId"
        :selectId="formData.auth"
    ></module-dialog>
</template>
<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import { addAppkit, getAppkitDetail, putAppkit } from '@/api/appkit.js'
import { ElMessage } from 'element-plus'
import { validateUrl } from '@/utils/rules.js'
import { getAuthOptionLists } from '@/api/auth'
import moduleDialog from './moduleDialog.vue'
import { useRouter } from 'vue-router'
import { getPostFormData } from '@/utils/common'

const router = useRouter()

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    isForm: {
        type: Boolean,
        default: false
    },
    formId: {
        type: String,
        required: false
    },
    group: {
        type: String,
        required: false
    },
    modList: {
        type: Array,
        default: () => []
    },
    groupList: {
        type: Array,
        default: () => []
    }
})
const emits = defineEmits(['update:modelValue', 'updateForm'])
const avatarEditLists = computed(() => {
    return formData.value.icon_url ? [{ name: formData.value.icon, url: formData.value.icon_url }] : []
})

// 表单源
const formData = ref({
    id: '',
    name: '',
    kit_type: 1,
    icon: '',
    auth: '',
    url: '',
    remark: '',
    group: props.group,
    list_order: 1,
    status: 1,
    visible: 1,
    auth_name: '',
    group_name: ''
})
const moduleDialogVisible = ref(false)
const modList = ref([])
const groupList = ref([])
// 数据类型

const moudleIsForm = ref(false) // 是否是表单
// 验证规则
const formRules = ref({
    name: [
        {
            required: true,
            trigger: 'blur',
            message: '请输入套件名称'
        }
    ],
    auth: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择关联菜单'
        }
    ],
    group: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择所属分组'
        }
    ],
    auth_name: [
        {
            required: true,
            // trigger: 'blur',
            message: '请选择关联菜单'
        }
    ],
    icon: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择图标'
        }
    ],
    kit_type: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择套件类型'
        }
    ],
    url: [{ required: true, validator: validateUrl, trigger: 'blur' }]
})
const dialogTitle = ref('')
const formRef = ref(null)
const authListsData = ref([])
const submitLoading = ref(false)

// 跳转
const urlPush = async () => {
    router.push({ path: formData.value.url })
}
// 选择菜单dialog
const handleSelectModule = async () => {
    moudleIsForm.value = true
    moduleDialogVisible.value = !!props.isForm
}
// 上传图标
const checkAvatarUpload = (file) => {
    const suffixIndex = file.name.lastIndexOf('.')
    const suffix = file.name.substr(suffixIndex + 1).toLowerCase()
    const allowExt = ['jpg', 'png']
    const isEXT = allowExt.indexOf(suffix) >= 0
    const isLt2M = file.size / 1024 / 1024 < 2

    if (!isEXT) {
        ElMessage.error('必须上传 jpg/png 格式的图片！')
        return false
    }
    if (!isLt2M) {
        ElMessage.error('图片不许超过 2MB!')
        return false
    }
    return true
}

// handleAvatarChange
const handleAvatarChange = (file, fileList) => {
    if (!checkAvatarUpload(file.raw)) {
        avatarUploader.value.handleRemove(file)
        return
    }
    formData.value.icon = file.raw
}
// // handleAvatarRemove
const handleAvatarRemove = (file, fileList) => {
    formData.value.icon_url = ''
}

// // 文件选择超出限制
const avatarUploader = ref()
const handleAvatarExceed = (files) => {
    ElMessage.warning('超限啦，只允许上传一张图片！')
}

// 关闭
const closed = () => {
    dialogTitle.value = ''
    emits('update:modelValue', false)
    formRef.value.resetFields()
}
const getAuthId = async (moduleName) => {
    formData.value.auth_name = moduleName.label
    formData.value.auth = moduleName.value
}

// 确定按钮点击事件
const onConfirm = async () => {
    if (!props.isForm) return
    if (props.formId !== '') {
        await doputAppkit()
    } else {
        await doaddAppkit()
    }
}
// 修改
const doputAppkit = async () => {
    // 进行表单校验
    formRef.value.validate(async (valid) => {
        if (!valid) return
        submitLoading.value = true
        try {
            const postFormData = getPostFormData()
            await putAppkit(postFormData)
            // 修改成功
            emits('updateForm')
            ElMessage.success('修改成功！')
            submitLoading.value = false
            closed()
        } catch (error) {
            submitLoading.value = false
        }
    })
}

// 添加
const doaddAppkit = async () => {
    // 进行表单校验
    formRef.value.validate(async (valid) => {
        if (!valid) return
        submitLoading.value = true
        try {
            const postFormData = getPostFormData()
            await addAppkit(postFormData)
            // 添加成功
            emits('updateForm')
            ElMessage.success('添加成功！')
            submitLoading.value = false
            closed()
        } catch (error) {
            submitLoading.value = false
        }
    })
}

// 获取详情
const getConfigItemDetailFun = async () => {
    const data = await getAppkitDetail(props.formId)
    dialogTitle.value = '-' + data.name
    formRef.value.resetFields()
    formData.value = data
    props.groupList.forEach((v) => {
        if (v.id === formData.value.group) {
            formData.value.group_name = v.name
        }
    })
}

// 获取菜单权限详情
const getAuthListsData = async (mod) => {
    const data = await getAuthOptionLists({ auth_type: 2, mod })
    authListsData.value = data
}

// 只有formId有的时候，才会去请求数据
watch(
    () => props.modelValue,
    (newVal) => {
        modList.value = props.modList.map((v) => ({ label: v.name, value: v.id }))
        groupList.value = props.groupList.map((v) => ({ label: v.name, value: v.id }))
        if (newVal && props.formId) {
            getConfigItemDetailFun()
            getAuthListsData()
        } else {
            formData.value = {
                id: '',
                name: '',
                kit_type: 1,
                icon: '',
                auth: '',
                url: '',
                remark: '',
                group: props.group,
                list_order: 1,
                status: 1,
                visible: 1,
                auth_name: '',
                group_name: ''
            }
        }
    }
)
</script>

<style lang="scss" scoped>
.el-icon {
    margin-top: 9px;
}
.avatar {
    width: 100px;
}

:deep(.el-upload-list__item-preview),
:deep(.el-icon--zoom-in) {
    display: none;
}
.name {
    color: #337ab7;
    cursor: pointer;
}
</style>
